import { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import TipItem from '../components/TipItem';
import { useTipsFeed } from '../hooks/useTipsFeed';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import FeatureGuideOverlay from '@/components/ui/FeatureGuideOverlay';

const PASTEL_COLORS = [
  'bg-blue-100',
  'bg-green-100',
  'bg-amber-100',
  'bg-rose-100',
  'bg-purple-100',
  'bg-cyan-100',
  'bg-orange-100',
  'bg-pink-100',
];

const TIPS_PAGE_GUIDE_STEPS = [
  {
    id: 'tips-page-overview',
    title: 'نکات منتخب کتاب‌ها',
    description: 'اینجا تیکه‌های منتخب کتاب‌ها رو می‌خونی و اگر دوست داشتی لایک می‌کنی یا به اشتراک می‌گذاریشون.',
  },
];

const TipsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, error, fetchNextPage, isFetchingNextPage } = useTipsFeed();

  const tips = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.items);
  }, [data]);

  const handleScroll = () => {
    if (containerRef.current && tips.length > 0) {
      const { scrollTop, clientHeight } = containerRef.current;
      const index = Math.round(scrollTop / clientHeight);
      if (index !== activeIndex) {
        setActiveIndex(index);
      }

      if (index >= tips.length - 2 && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground font-persian">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-muted-foreground font-persian">خطا در بارگذاری نکات</p>
        </div>
      </div>
    );
  }

  if (tips.length === 0) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-4xl mb-4">💡</div>
          <p className="text-muted-foreground font-persian">هنوز نکته‌ای وجود ندارد</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 w-full bg-gray-50 overflow-hidden"
      style={{
        height: '100dvh',
      }}
    >
      <button
        onClick={() => navigate('/dashboard')}
        className="absolute z-50 p-1.5 bg-white/80 backdrop-blur-sm hover:bg-white rounded-full shadow-lg transition-all"
        style={{
          top: 'calc(1rem + env(safe-area-inset-top))',
          right: '1rem',
        }}
        aria-label="بستن"
      >
        <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="w-full overflow-y-scroll snap-y snap-mandatory overscroll-y-contain"
        style={{
          height: '100dvh',
          scrollBehavior: 'smooth',
        }}
      >
        {tips.map((tip, index) => (
          <TipItem
            key={`${tip.tips_id}-${index}`}
            tipId={tip.tips_id}
            content={tip.content}
            title={tip.book_title}
            color={PASTEL_COLORS[index % PASTEL_COLORS.length]}
            isActive={index === activeIndex}
            bookId={tip.book_id}
            coverImage={tip.book_cover_image_url || undefined}
            bookCategory={tip.book_category || undefined}
            contentType={tip.content_type || 'non_fiction'}
            likesCount={tip.likes_count}
            sharesCount={tip.shares_count}
            isLiked={tip.is_liked}
          />
          ))}
      </div>

      {user?.is_registered && !user.is_guest && (
        <FeatureGuideOverlay
          steps={TIPS_PAGE_GUIDE_STEPS}
        />
      )}
    </div>
  );
};

export default TipsPage;
